import { defineStore } from 'pinia'

import { CategoryType } from '~/enums/category-type'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { CategoryData } from '~/types/categories'

export const messages = {
  failedToGetCategories: 'Не удалось получить категории.',
  emptyCategories: 'Нет категорий',
}

export type State = {
  filter: {
    query: string
    typeIds: CategoryType[]
  }
  isDataLoading: boolean
  page: {
    number: number
    size: number
  }
  categoriesData: CategoryData[]
  totalPages: number | null
  totalCategories: number | null
}

const initialState = (): State => ({
  filter: {
    query: '',
    typeIds: []
  },
  isDataLoading: false,
  page: {
    number: 1,
    size: 20
  },
  categoriesData: [],
  totalPages: null,
  totalCategories: null
})

export const createCategoriesStore = (id: string) => defineStore(`categories-${id}`, {
  state: () => initialState(),
  getters: {
    getActiveIngredientCategories (state): CategoryData [] {
      return state.categoriesData.filter(
        (x: CategoryData) => x.attributes.typeId === CategoryType.ActiveIngredient
      )
    },
    getBodySystemCategories (state): CategoryData[] {
      return state.categoriesData.filter(
        (x: CategoryData) => x.attributes.typeId === CategoryType.BodySystem
      )
    },
    getCategories: (state: State) => state.categoriesData
  },
  actions: {
    clearFilter (): void {
      this.filter = initialState().filter
    },
    clearCategories (): void {
      this.categoriesData = []
    },
    deleteCategoryById (categoryId: string): void {
      const index = this.categoriesData.findIndex((x: CategoryData) => x.id === categoryId)
      this.categoriesData.splice(index, 1)
    },
    dispose (): void {
      this.clearFilter()
      this.clearCategories()
    },
    async fetchCategories ({ signal }: { signal?: AbortSignal } = {}): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      this.isDataLoading = true
      this.totalPages = null

      const params = helpers.getParams([
        { name: 'filter[query]', value: this.filter.query, condition: true },
        ...this.filter.typeIds.map((typeId: string) => ({
          name: 'filter[typesIds][]',
          value: typeId,
          condition: true
        })),
        { name: 'page[number]', value: this.page.number, condition: true },
        { name: 'page[size]', value: this.page.size, condition: true },
      ]);

      try {
        const response = await $apiHelper.categories.getCategories(params, signal)
        const { data, meta } = JsonApiHelper.denormalizeResponse(response)

        this.totalCategories = meta.totalCategories
        this.totalPages = helpers.getTotalPages(meta.totalCategories, this.page.size)

        for (let i = 0; i < data.length; i++) {
          if (!this.categoriesData.some((x: CategoryData): boolean => x.id === data[i].id)) {
            this.categoriesData.push(data[i])
          }
        }
      } catch (error: any) {
        if (!signal?.aborted && error.name !== 'AbortError' && error.response) {
          console.error(error)
          $toast.error(messages.failedToGetCategories)
        }
      }

      this.isDataLoading = false
    },
  }
})
